import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { SUPPORTED_LANGS, DEFAULT_LANG, SITE_ORIGIN, absoluteUrl, isRtl } from '../../utils/locale';

const DEFAULT_IMAGE = `${SITE_ORIGIN}/og-image.jpg`;

const OG_LOCALE = {
  en: 'en_US',
  ar: 'ar_AR',
  he: 'he_IL',
};

const normalizeLang = (raw) => {
  const lang = (raw || DEFAULT_LANG).split('-')[0];
  return SUPPORTED_LANGS.includes(lang) ? lang : DEFAULT_LANG;
};

/**
 * SEO component — drop into any page to set per-page meta.
 *
 * Props:
 *   title        – page title (appends "| Stylon" if not already included)
 *   description  – meta description, 150-160 chars
 *   canonical    – LOGICAL path string e.g. "/features/crm" (language prefix is
 *                  added automatically per active locale)
 *   ogImage      – absolute URL for OG image (defaults to og-image.jpg)
 *   noindex      – true to add noindex,nofollow (legal / private pages)
 *   jsonLd       – array of schema.org objects to inject as JSON-LD scripts
 *
 * Multilingual SEO:
 *   – Canonical points to the URL in the CURRENT language
 *   – hreflang alternates point to all three language variants of the same
 *     logical path (en at root, /he/..., /ar/...) plus an x-default → en
 *   – og:locale + og:locale:alternate reflect the active language
 */
/**
 * If a meta value is a plain string, return it as-is. If it's a per-language
 * map (e.g. `{ en, he, ar }`), pick the active language with English fallback.
 * Lets callers incrementally localize page metadata without changing the
 * SEO call site — `<SEO title={HOME_META.title} ...>` works for both shapes.
 */
function pickLocalized(value, lang) {
  if (value == null) return value;
  if (typeof value === 'string') return value;
  if (typeof value === 'object') return value[lang] || value.en || '';
  return value;
}

const SEO = ({
  title,
  description,
  canonical,
  ogImage = DEFAULT_IMAGE,
  noindex = false,
  jsonLd = [],
}) => {
  const { i18n } = useTranslation();
  const lang = normalizeLang(i18n.language);
  const ogLocale = OG_LOCALE[lang] || OG_LOCALE.en;
  const alternateOgLocales = Object.entries(OG_LOCALE)
    .filter(([code]) => code !== lang)
    .map(([, val]) => val);

  const titleStr = pickLocalized(title, lang) || '';
  const descriptionStr = pickLocalized(description, lang) || '';

  const fullTitle = titleStr && titleStr.toLowerCase().includes('stylon')
    ? titleStr
    : `${titleStr} | Stylon`;

  // canonical is a LOGICAL path (no lang prefix). Build per-language absolute URLs.
  const logicalPath = canonical || '/';
  const canonicalUrl = absoluteUrl(logicalPath, lang);
  const hreflangAlternates = SUPPORTED_LANGS.map((l) => ({
    hreflang: l,
    href: absoluteUrl(logicalPath, l),
  }));
  const xDefault = absoluteUrl(logicalPath, DEFAULT_LANG);

  // Annotate page-shaped JSON-LD with inLanguage so search engines + LLM crawlers
  // know the active language even on same-URL render. Leaves Organization /
  // SoftwareApplication / FAQPage etc. untouched (they're locale-agnostic).
  const annotatedJsonLd = jsonLd.map((schema) => {
    if (!schema || typeof schema !== 'object') return schema;
    return { ...schema, inLanguage: schema.inLanguage || lang };
  });

  return (
    <Helmet defer={false}>
      <html lang={lang} dir={isRtl(lang) ? 'rtl' : 'ltr'} />
      <title>{fullTitle}</title>
      <meta name="description" content={descriptionStr} />
      <link rel="canonical" href={canonicalUrl} />
      {noindex
        ? <meta name="robots" content="noindex, nofollow" />
        : <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />}

      {/* hreflang alternates: distinct URL per language + x-default → en root */}
      {hreflangAlternates.map(({ hreflang, href }) => (
        <link key={hreflang} rel="alternate" hrefLang={hreflang} href={href} />
      ))}
      <link rel="alternate" hrefLang="x-default" href={xDefault} />

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={descriptionStr} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Stylon" />
      <meta property="og:locale" content={ogLocale} />
      {alternateOgLocales.map((loc) => (
        <meta key={loc} property="og:locale:alternate" content={loc} />
      ))}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={descriptionStr} />
      <meta name="twitter:image" content={ogImage} />

      {/* JSON-LD structured data */}
      {annotatedJsonLd.map((schema, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
};

export default SEO;
